import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Users, Clock, Scissors, ArrowRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const statusClass = {
  confirmed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  cancelled: "bg-red-500/10 text-red-400 border-red-500/20",
};

export default function DashboardPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  const statConfig = [
    { key: "today_appointments", label: t("dashboard_today"), icon: CalendarDays, color: "text-indigo-400", bg: "bg-indigo-500/10" },
    { key: "total_appointments", label: t("dashboard_total"), icon: Users, color: "text-emerald-400", bg: "bg-emerald-500/10" },
    { key: "pending_appointments", label: t("dashboard_pending"), icon: Clock, color: "text-amber-400", bg: "bg-amber-500/10" },
    { key: "total_services", label: t("dashboard_services"), icon: Scissors, color: "text-violet-400", bg: "bg-violet-500/10" },
  ];

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => toast.error(t("dashboard_loading")));
  }, []);

  if (!stats) return <div className="flex items-center justify-center h-64 text-slate-500">{t("dashboard_loading")}</div>;

  return (
    <div data-testid="dashboard-page" className="space-y-8 animate-fade-in">
      <h1 className="text-2xl font-bold tracking-tight font-heading text-white">{t("dashboard_title")}</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statConfig.map(({ key, label, icon: Icon, color, bg }) => (
          <Card key={key} className="bg-slate-900/50 border-slate-800" data-testid={`stat-${key}`}>
            <CardContent className="p-5">
              <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                <Icon size={18} className={color} strokeWidth={1.5} />
              </div>
              <div className="text-2xl font-bold text-white font-mono">{stats[key]}</div>
              <div className="text-xs text-slate-500 mt-1">{label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white font-heading flex items-center justify-between">
              {t("dashboard_todays_schedule")}
              <button onClick={() => navigate("/calendar")} className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1" data-testid="view-calendar-link">
                {t("dashboard_view_all")} <ArrowRight size={12} />
              </button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.todays_list.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">{t("dashboard_no_today")}</p>
            ) : (
              <div className="space-y-2">
                {stats.todays_list.map((apt) => (
                  <div key={apt.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/40 border border-slate-700/30" data-testid={`today-apt-${apt.id}`}>
                    <div className="w-1 h-10 rounded-full shrink-0" style={{ backgroundColor: apt.service_color || "#6366f1" }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">{apt.customer_name}</div>
                      <div className="text-xs text-slate-400">{apt.service_name}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-mono text-slate-300">{apt.start_time}</div>
                      <Badge className={`text-[10px] border mt-0.5 ${statusClass[apt.status] || ""}`}>{apt.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white font-heading flex items-center justify-between">
              {t("dashboard_upcoming")}
              <button onClick={() => navigate("/appointments")} className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1" data-testid="view-appointments-link">
                {t("dashboard_view_all")} <ArrowRight size={12} />
              </button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.upcoming.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">{t("dashboard_no_upcoming")}</p>
            ) : (
              <div className="space-y-2">
                {stats.upcoming.slice(0, 6).map((apt) => (
                  <div key={apt.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/40 border border-slate-700/30">
                    <div className="w-1 h-10 rounded-full shrink-0" style={{ backgroundColor: apt.service_color || "#6366f1" }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">{apt.customer_name}</div>
                      <div className="text-xs text-slate-400">{apt.service_name}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-slate-400">{format(parseISO(apt.date), "MMM d")}</div>
                      <div className="text-sm font-mono text-slate-300">{apt.start_time}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
